import { useState } from 'react'
import ButtonCustom from '../../components/atoms/button/ButtonCustom'
import CommonModal from '../../components/atoms/modal/CommonModal'
import MultiselectSearch from '../../components/atoms/select/MultiSelect'
import styles from './ChatPageV2.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { toastHelper } from '../../util/toastHelper'
import { addGroupMember, createGroup } from '../../features/groupSlice'
import { useEffect, useMemo } from 'react'
import { getFriendsInfo } from '../../features/friendSlice'
import { useParams } from 'react-router-dom'
import { getDirectChatUsers, getNewChatUsers } from '../../features/messageSlice'

export function GroupModal({ handleClose, isNewGroup }) {
	const dispatch = useDispatch()
	const { kazooId } = useParams()

	const groupMembers = useSelector((s) => s.group.groupMembers)
	const groupId = useSelector((s) => s.group.currentSelectedGroup?._id)
	const rawFriends = useSelector((s) => s.message.newChatUserList)

	useEffect(() => {
		dispatch(
			getNewChatUsers({
				chatType: kazooId ? 'inSide' : 'outSide',
				kazooId: kazooId || '',
				isGroupChat: false,
			})
		)
	}, [dispatch, kazooId])

	const friends = useMemo(() => {
		return rawFriends
			.filter((member) => !member.status)
			.map((member) => {
				const displayName = member.businessName || member.preferredName || `${member.firstName} ${member.lastName}`
				const fullName = `${member.firstName} ${member.lastName}`
				return {
					...member,
					name: displayName, searchName: `${displayName} ${fullName} ${member.businessName || ''} ${member.email || ''}`
				}
			})
	}, [rawFriends])

	const [selectableFriends, setSelectableFriends] = useState(friends)

	const [selected, setSelected] = useState([])
	const [groupName, setGroupName] = useState('')

	// Update selectable friends when the redux store updates (e.g. data load)
	useEffect(() => {
		const selectedIds = new Set(selected.map(s => s._id))
		// Verify we don't duplicate or add back selected ones
		const available = friends.filter(f => !selectedIds.has(f._id))
		// Also exclude current group members if editing
		if (!isNewGroup && groupMembers) {
			const memberIds = new Set(groupMembers.map((member) => member._id))
			setSelectableFriends(
				available.filter(
					(friend) => !memberIds.has(friend._id)
				)
			)
		} else {
			setSelectableFriends(available)
		}
	}, [friends, isNewGroup, groupMembers])

	const handleOnSuccess = () => {
		if (selected.length === 0)
			return toastHelper('Must add at least one user', 'error')

		if (isNewGroup) {
			if (!groupName.trim())
				return toastHelper('Group name is required', 'error')
			dispatch(
				createGroup({
					name: groupName,
					memberIds: selected.map((member) => ({
						memberId: member._id,
					})),
					...(kazooId && { kazooId }),
				})
			)

			setGroupName('')
		} else {
			dispatch(addGroupMember({ groupId, userIds: selected }))
		}

		handleClose()
	}

	const handleSelect = (member) => {
		setSelected((prev) => [...prev, member])
		setSelectableFriends(
			selectableFriends.filter((m) => m._id !== member._id)
		)
	}

	const handleRemove = (id) => {
		setSelected((prev) =>
			prev.filter((m) => {
				if (m._id === id)
					setSelectableFriends([...selectableFriends, m])
				return m._id !== id
			})
		)
	}

	const handleGroupNameChange = (e) => {
		setGroupName(e.target.value)
	}

	return (
		<CommonModal
			open={true}
			handleClose={handleClose}
			heading={isNewGroup ? 'Start a New Group Chat' : 'Add Members'}
			footerContent={
				<>
					<ButtonCustom
						onClick={handleClose}
						variant="outlined"
						size="lg"
					>
						Cancel
					</ButtonCustom>
					<ButtonCustom size="lg" onClick={handleOnSuccess}>
						{isNewGroup ? 'Create Group' : 'Add'}
					</ButtonCustom>
				</>
			}
		>
			<div className={styles.groupModal}>
				{isNewGroup && (
					<input
						value={groupName}
						type="text"
						name="group_title"
						id="group_title"
						placeholder="Enter group title"
						maxLength={30}
						className={styles.groupTitleInput}
						onChange={handleGroupNameChange}
					/>
				)}
				{isNewGroup && (
					<p className={styles.subHeader}>Add Members</p>
				)}
				<MultiselectSearch
					members={selectableFriends}
					selected={selected}
					onSelect={handleSelect}
					onRemove={handleRemove}
					placeholder="Select"
					searchPlaceholder="Search users..."
					slotProps={{
						paper: {
							sx: {
								position: "relative",
								maxHeight: "150px",
								overflowY: 'auto',
							}
						}
					}}
				/>
			</div>
		</CommonModal>
	)
}
